import { AvailabilityType } from '../enums/AvailabilityType';
import { ProjectPreferenceTime } from '../enums/projectPreferenceTime';
import { PROFICIENCY_DESCRIPTIONS } from '../interfaces/language';
import { TEXTS } from './texts';
import { CustomerStatus } from '../enums/customerStatus';

export namespace Validator {

  export const LENGTHS: { [key in NAMES]: { [key: string]: { min: number, max?: number } } } = {
    AUTH: {
      password: { min: 8 },
      email: { min: 1 }
    },
    EXPERIENCE: {
      company: { min: 1, max: 200 },
      role: { min: 1, max: 200 },
      description: { min: 1, max: 500 }
    },
    EDUCATION: {
      name: { min: 1, max: 200 },
      town: { min: 1, max: 50 },
      institution: { min: 1, max: 150 },
      country: { min: 3, max: 3 },
      description: { min: 1, max: 500 }
    },
    CERTIFICATE: {
      name: { min: 1, max: 60 },
      description: { min: 0, max: 300 },
      source: { min: 0, max: 200 },
      // fileName: { min: 1, max: 100 }
    },
    AVAILABILITY: {
      type: { min: 1 },
      availabilityType: { min: 1 },
      weeklyHoursAvailable: { min: 1 },
      projectPreferenceType: { min: 1 },
      nextProjectStart: { min: 1 },
      fromHour: { min: 1 },
      toHour: { min: 1 },
      timezone: { min: 1 },
      rateAmount: { min: 1 },
      rateCurrency: { min: 1 },
      rateType: { min: 1 },
      amount: { min: 1 },
    },
    LANGUAGE: {
      languageCode: { min: 2, max: 2 },
      proficiencyLevel: { min: 1 },
      minimumProficiencyLevel: { min: 1 }
    },
    PROFILE: {
      firstName: { min: 1, max: 100 },
      lastName: { min: 1, max: 100 },
      phone: { min: 1, max: 50 },
      country: { min: 3, max: 3 },
      town: { min: 1, max: 50 },
      summary: { min: 0, max: 500 },
      roleId: { min: 1 },
      positionId: { min: 1 }
    },
    CLIENT: {
      contactName: { min: 1, max: 200 },
      legalName: { min: 1, max: 200 },
      email: { min: 1, max: 255 },
      phone: { min: 1, max: 50 }
    },
    PROJECT: {
      title: { min: 1, max: 150 },
      overview: { min: 0, max: 3000 },
      clientId: { min: 1 }
    },
    POSITION: {
      roleId: { min: 1 },
      positionId: { min: 1 },
      title: { min: 1, max: 150 },
      description: { min: 1, max: 2500 },
      link: { min: 0, max: 200 },
      skills: { min: 0 }
    },
    RATE: {
      type: { min: 1 },
      amount: { min: 1 },
      currency: { min: 1 },
    }
  };

  export enum NAMES {
    AUTH = 'AUTH',
    CERTIFICATE = 'CERTIFICATE',
    EDUCATION = 'EDUCATION',
    EXPERIENCE = 'EXPERIENCE',
    AVAILABILITY = 'AVAILABILITY',
    LANGUAGE = 'LANGUAGE',
    PROFILE = 'PROFILE',
    CLIENT = 'CLIENT',
    PROJECT = 'PROJECT',
    POSITION = 'POSITION',
    RATE = 'RATE'
  }

  const check = (name: NAMES, key: string, val: string, msg?: string): null | string => {
    const constraints = LENGTHS[name][key];
    if (constraints) {
      if (!val) {
        return constraints.min ? 'Required field' : null;
      }
      if (val.length < constraints.min) {
        return msg || (constraints.min === 1 || !val.length ? 'Required field' : `Minimum ${constraints.min} chars required`);
      }
      if (constraints.max && val.length > constraints.max) {
        return msg || `Maximum ${constraints.max} chars allowed`;
      }
    }
    return null;
  };

  export const CONSTRAINTS: { [key in NAMES]: { [key: string]: (val: any) => null | string } } = {
    AUTH: {
      password: (val: string) => check(NAMES.AUTH, 'password', val),
      oldPassword: (val: string) => check(NAMES.AUTH, 'password', val),
      rePassword: (val: string) => check(NAMES.AUTH, 'password', val),
      email: (val: string) => check(NAMES.AUTH, 'email', val)
    },
    EDUCATION: {
      name: (val: string) => check(NAMES.EDUCATION, 'name', val),
      country: (val: string) => check(NAMES.EDUCATION, 'country', val, 'Please select a country from the list'),
      town: (val: string) => check(NAMES.EDUCATION, 'town', val, 'Please select a town from the list'),
      description: (val: string) => check(NAMES.EDUCATION, 'description', val),
      institution: (val: string) => check(NAMES.EDUCATION, 'institution', val),
    },
    EXPERIENCE: {
      company: (val: string) => check(NAMES.EXPERIENCE, 'company', val),
      role: (val: string) => check(NAMES.EXPERIENCE, 'role', val),
      description: (val: string) => check(NAMES.EXPERIENCE, 'description', val),
    },
    CERTIFICATE: {
      name: (val: string) => check(NAMES.CERTIFICATE, 'name', val),
      // fileName: (val: string) => check(NAMES.CERTIFICATE, 'fileName', val, 'File uploading is required')
    },
    AVAILABILITY: {
      type: (val: string) => Object.values(AvailabilityType).includes(val as any) ? null : TEXTS.INVALID_TYPE,
      availabilityType: (val: string) => Object.values(AvailabilityType).includes(val as any) ? null : TEXTS.INVALID_TYPE,
      weeklyHoursAvailable: (val: string) => !!val && +val > 0 ? null : 'Invalid value',
      projectPreferenceType: (val: string) => Object.values(ProjectPreferenceTime).includes(val as any) ? null : TEXTS.INVALID_TYPE,
      nextProjectStart: (val: string) => check(NAMES.AVAILABILITY, 'nextProjectStart', val),
      fromHour: (val: string) => check(NAMES.AVAILABILITY, 'fromHour', val),
      toHour: (val: string) => check(NAMES.AVAILABILITY, 'toHour', val),
      timezone: (val: string) => check(NAMES.AVAILABILITY, 'timezone', val),
      description: (val: string) => check(NAMES.AVAILABILITY, 'description', val),
      rateAmount: (val: string) => !!val && +val > 0 ? null : 'Invalid value',
      rateCurrency: (val: string) => check(NAMES.AVAILABILITY, 'rateCurrency', val),
      rateType: (val: string) => check(NAMES.AVAILABILITY, 'rateType', val),
    },
    LANGUAGE: {
      languageCode: (val: string) => check(NAMES.LANGUAGE, 'languageCode', val),
      proficiencyLevel: (val: string) => (+val >= 0 && Object.values(PROFICIENCY_DESCRIPTIONS).length >= +val)
        ? null : TEXTS.INVALID_TYPE,
      minimumProficiencyLevel: (val: string) => Object.values(PROFICIENCY_DESCRIPTIONS).includes(val as any) ? null : TEXTS.INVALID_TYPE
    },
    PROFILE: {
      firstName: (val: string) => check(NAMES.PROFILE, 'firstName', val),
      lastName: (val: string) => check(NAMES.PROFILE, 'lastName', val),
      phone: (val: string) => check(NAMES.PROFILE, 'phone', val),
      country: (val: string) => check(NAMES.PROFILE, 'country', val),
      town: (val: string) => check(NAMES.PROFILE, 'town', val),
      summary: (val: string) => check(NAMES.PROFILE, 'summary', val),
      status: (val: string) => Object.values(CustomerStatus).includes(val as any) ? null : TEXTS.INVALID_TYPE,
      roleId: (val?: number) => !!val && !isNaN(+val) && val >= 0 ? null : TEXTS.INVALID_TYPE,
      positionId: (val?: number) => !!val && !isNaN(+val) && val >= 0 ? null : TEXTS.INVALID_TYPE
    },
    CLIENT: {
      contactName: (val: string) => check(NAMES.CLIENT, 'contactName', val),
      legalName: (val: string) => check(NAMES.CLIENT, 'legalName', val),
      email: (val: string) => check(NAMES.CLIENT, 'email', val),
      phone: (val: string) => check(NAMES.CLIENT, 'phone', val)
    },
    PROJECT: {
      title: (val: string) => check(NAMES.PROJECT, 'title', val),
      overview: (val: string) => check(NAMES.PROJECT, 'overview', val),
      clientId: (val: string) => check(NAMES.PROJECT, 'clientId', val),
    },
    POSITION: {
      roleId: (val: string) => check(NAMES.POSITION, 'roleId', val),
      positionId: (val: string) => check(NAMES.POSITION, 'positionId', val),
      title: (val: string) => check(NAMES.POSITION, 'title', val),
      description: (val: string) => check(NAMES.POSITION, 'description', val),
      link: (val: string) => check(NAMES.POSITION, 'link', val),
      skills: (val: string[]) => !!val.length ? null : 'Skills must be not empty'
    },
    RATE: {
      amount: (val: string) => !!val && +val > 0 ? null : 'Invalid value',
      currency: (val: string) => check(NAMES.RATE, 'currency', val),
      type: (val: string) => check(NAMES.RATE, 'type', val),
    }
  };

  export const validate = (state: any, name: NAMES): { [key: string]: (string | null) } | null => {
    if (typeof state !== 'object' || Array.isArray(state)) { return null; }
    const errors = Object.keys(state || {}).reduce((acc: { [key: string]: (string | null) }, cur) => {
      if (CONSTRAINTS[name][cur]) {
        acc[cur] = CONSTRAINTS[name][cur](state[cur]);
      }
      return acc;
    }, {});
    if (state.password && state.rePassword && state.password !== state.rePassword) {
      errors['rePassword'] = `Passwords don't match`;
    }
    if (Object.values(errors).find(e => !!e)) {
      return errors;
    }
    return null;
  };

}









